import { TooltipCoordsType, TooltipCordType } from '../../types';
import { visibilityChangingFrameInterval } from './constants';

const getCordByTwoPoints = (
    point1: { cord: number; frame: number },
    point2: { cord: number; frame: number },
    targetFrame: number,
): number => {
    return (
        ((point2.cord - point1.cord) / (point2.frame - point1.frame)) *
            (targetFrame - point1.frame) +
        point1.cord
    );
};

const getNullAndMaxFramePoints = (
    coords: TooltipCoordsType,
    verySmallFrame: number,
    veryBigFrame: number,
    framesMaxIndex: number,
): TooltipCordType => {
    const coordsDifferenceBetweenMinAndMaxFrames = {
        x:
            (coords.get(verySmallFrame) as TooltipCordType).x -
            (coords.get(veryBigFrame) as TooltipCordType).x,
        y:
            (coords.get(verySmallFrame) as TooltipCordType).y -
            (coords.get(veryBigFrame) as TooltipCordType).y,
    };

    const xShift =
        (verySmallFrame * Math.abs(coordsDifferenceBetweenMinAndMaxFrames.x)) /
        (framesMaxIndex - veryBigFrame + verySmallFrame);
    const yShift =
        (verySmallFrame * Math.abs(coordsDifferenceBetweenMinAndMaxFrames.y)) /
        (framesMaxIndex - veryBigFrame + verySmallFrame);

    return {
        x:
            (coords.get(verySmallFrame) as TooltipCordType).x +
            (coordsDifferenceBetweenMinAndMaxFrames.x > 0 ? -1 * xShift : xShift),
        y:
            (coords.get(verySmallFrame) as TooltipCordType).y +
            (coordsDifferenceBetweenMinAndMaxFrames.y > 0 ? -1 * yShift : yShift),
    };
};

const getCoordsForFrameBetweenMinAndMax = (
    frameIndexes: number[],
    frame: number,
    coords: TooltipCoordsType,
) => {
    const smallerClosestFrame = frameIndexes.filter(frameIndex => frameIndex < frame).reverse()[0];
    const biggerClosestFrame = frameIndexes.filter(frameIndex => frameIndex > frame)[0];

    return {
        x: getCordByTwoPoints(
            {
                cord: (coords.get(smallerClosestFrame) as TooltipCordType).x,
                frame: smallerClosestFrame,
            },
            {
                cord: (coords.get(biggerClosestFrame) as TooltipCordType).x,
                frame: biggerClosestFrame,
            },
            frame,
        ),
        y: getCordByTwoPoints(
            {
                cord: (coords.get(smallerClosestFrame) as TooltipCordType).y,
                frame: smallerClosestFrame,
            },
            {
                cord: (coords.get(biggerClosestFrame) as TooltipCordType).y,
                frame: biggerClosestFrame,
            },
            frame,
        ),
    };
};

const getCoordsForFrameOuterMinAndMax = (
    frameIndexes: number[],
    frame: number,
    verySmallFrame: number,
    veryBigFrame: number,
    framesMaxIndex: number,
    coords: TooltipCoordsType,
) => {
    const nullAndMaxFramePoints =
        coords.get(0) ||
        coords.get(framesMaxIndex) ||
        getNullAndMaxFramePoints(coords, verySmallFrame, veryBigFrame, framesMaxIndex);

    if (frame > veryBigFrame) {
        return {
            x: getCordByTwoPoints(
                { cord: (coords.get(veryBigFrame) as TooltipCordType).x, frame: veryBigFrame },
                { cord: nullAndMaxFramePoints.x, frame: framesMaxIndex },
                frame,
            ),
            y: getCordByTwoPoints(
                { cord: (coords.get(veryBigFrame) as TooltipCordType).y, frame: veryBigFrame },
                { cord: nullAndMaxFramePoints.y, frame: framesMaxIndex },
                frame,
            ),
        };
    } else {
        return {
            x: getCordByTwoPoints(
                { cord: (coords.get(verySmallFrame) as TooltipCordType).x, frame: verySmallFrame },
                { cord: nullAndMaxFramePoints.x, frame: 0 },
                frame,
            ),
            y: getCordByTwoPoints(
                { cord: (coords.get(verySmallFrame) as TooltipCordType).y, frame: verySmallFrame },
                { cord: nullAndMaxFramePoints.y, frame: 0 },
                frame,
            ),
        };
    }
};

const getCoords = (
    coords: TooltipCoordsType,
    framesMaxIndex: number,
    targetFrameIndex: number,
): TooltipCordType => {
    if (coords.has(targetFrameIndex)) {
        return coords.get(targetFrameIndex) as TooltipCordType;
    }

    if (coords.size === 1) {
        return { x: coords.values().next().value.x, y: coords.values().next().value.y };
    }

    const frameIndexes: number[] = [];
    for (const frame of coords.keys()) {
        frameIndexes[frameIndexes.length] = frame;
    }
    frameIndexes.sort((a, b) => (a > b ? 1 : -1));

    const verySmallFrame = Math.min(...frameIndexes);
    const veryBigFrame = Math.max(...frameIndexes);
    const isFrameBetweenMinAndMaxFrames =
        targetFrameIndex >= verySmallFrame && targetFrameIndex <= veryBigFrame;

    if (isFrameBetweenMinAndMaxFrames) {
        return getCoordsForFrameBetweenMinAndMax(frameIndexes, targetFrameIndex, coords);
    } else {
        return getCoordsForFrameOuterMinAndMax(
            frameIndexes,
            targetFrameIndex,
            verySmallFrame,
            veryBigFrame,
            framesMaxIndex,
            coords,
        );
    }
};

const getDegreeOfVisibility = (
    firstFrameIndex: number,
    lastFrameIndex: number,
    framesMaxIndex: number,
    targetFrameIndex: number,
): number => {
    const isFirstFrameIndexSmallerThanLast = firstFrameIndex < lastFrameIndex;

    const numberOfVisibleFrames = isFirstFrameIndexSmallerThanLast
        ? lastFrameIndex - firstFrameIndex
        : framesMaxIndex - lastFrameIndex + firstFrameIndex;

    if (numberOfVisibleFrames <= visibilityChangingFrameInterval * 2) {
        return 5;
    }

    const framesCountToClosestExtremeFrame = Math.min(
        Math.abs(lastFrameIndex - targetFrameIndex),
        Math.abs(firstFrameIndex - targetFrameIndex),
    );

    return Math.min(framesCountToClosestExtremeFrame + 1, 5);
};

export { getCoords, getDegreeOfVisibility };
